package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LCloseIcon
import dev.lingvo.site.ds.LCommentIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.LIconButton
import dev.lingvo.site.ds.LInfoIcon
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.rememberReducedMotion
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import kotlin.coroutines.resume
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.events.SyntheticKeyboardEvent
import org.w3c.dom.HTMLElement

@Composable
internal fun EditorUtilityPane(
    state: TranslationEditorState,
    surface: EditorUtilitySurface,
    shell: EditorUtilityShell,
    onSelectSurface: (EditorUtilitySurface, EditorUtilityActionOrigin) -> Unit,
    onRequestClose: (EditorUtilityActionOrigin) -> Unit,
    onAdvancePresence: (Long) -> Unit,
    onCompleteExit: (Long) -> Unit,
    onRequestFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
    onSuggestionInserted: (EditorSuggestionInsertion, EditorUtilityActionOrigin) -> Unit,
    currentActivationOrigin: () -> EditorUtilityActionOrigin = { EditorUtilityActionOrigin.KEYBOARD },
    discardActivationOrigin: () -> Unit = {},
    terminalFeedbackOccluded: Boolean = false,
) {
    require(surface != EditorUtilitySurface.NONE) { "EditorUtilityPane requires an active surface" }

    val presence = LocalEditorUtilityPresence.current
    val reducedMotion = rememberReducedMotion()
    val announcementsEnabled = !terminalFeedbackOccluded &&
        shell != EditorUtilityShell.MEASURING &&
        (presence.phase == EditorUtilityPresencePhase.ENTERING ||
            presence.phase == EditorUtilityPresencePhase.PRESENT)
    val row = state.selectedRow
    val draft = state.selectedDraft
    // Unknown until the comments land, and unknown draws nothing: a dot that appears a beat after
    // every key change, on every key, would be a dot that means "loading".
    val unresolvedComments = row?.let { selected ->
        (state.panels.comments[selected.key.id] as? Async.Success)?.value?.count { !it.resolved }
    } ?: 0
    var summaryExpanded by remember(row?.key?.id) { mutableStateOf(false) }
    val summaryModel = row?.let { selectedRow ->
        draft?.let { selectedDraft ->
            val allFindings = mergedQa(
                live = inlineQa(selectedRow, selectedDraft, state.locale),
                remote = state.qaIssues.filter { it.keyId == selectedRow.key.id },
            )
            editorCompactSummary(
                row = selectedRow,
                draft = selectedDraft,
                targetLocale = state.locale,
                category = state.pluralCategory.takeIf { selectedRow.key.isPlural },
                findings = if (selectedRow.key.isPlural) {
                    pluralQaForCategory(allFindings, state.pluralCategory)
                } else {
                    allFindings
                },
            )
        }
    } ?: EditorCompactSummaryModel(
        source = "",
        target = "",
        sourceLocale = state.project?.sourceLocale.orEmpty(),
        targetLocale = state.locale,
        pluralCategory = null,
        qaSeverity = "No QA issues",
    )

    LaunchedEffect(row?.key?.id, surface) {
        when (surface) {
            EditorUtilitySurface.DISCUSS -> row?.let { state.panels.ensureDiscussLoaded(it.key.id) }
            EditorUtilitySurface.ACTIVITY -> row?.let { state.panels.ensureActivityLoaded(it.key.id) }
            EditorUtilitySurface.NONE,
            EditorUtilitySurface.ASSIST,
            EditorUtilitySurface.DETAILS,
            -> Unit
        }
    }

    // **The dot needs the comments, so the pane fetches them whichever tab is open.**
    //
    // An attention dot that only appears once you have already opened Discuss tells you nothing
    // you did not know — the whole point of it is to say "there is a question on this key" to
    // somebody who is in Assist. `ensureDiscussLoaded` is idempotent and returns immediately once
    // a key is cached, so this is one request per key while the pane is open, not one per tab
    // switch. The measuring shell is excluded: it mounts a hidden second copy of this whole pane
    // to size it, and a hidden copy must not make requests.
    LaunchedEffect(row?.key?.id, shell) {
        if (shell == EditorUtilityShell.MEASURING) return@LaunchedEffect
        row?.let { state.panels.ensureDiscussLoaded(it.key.id) }
    }

    LaunchedEffect(
        presence.phase,
        presence.generation,
        presence.motionPolicy,
        reducedMotion,
        shell,
    ) {
        // **An exit in progress is never skipped, whatever the open decided.**
        //
        // This guard used to return for any `INSTANT` policy, and `onCompleteExit` is the only
        // caller of `completeUtilityExit` in the app — so a panel in EXITING with that policy never
        // finished exiting. It was reachable every day: `editorUtilityMotionPolicy` records INSTANT
        // for a keyboard open, and `closeUtility` only settles immediately for a keyboard *close*.
        // Open with Alt+H, close with the mouse, and the panel stayed in EXITING for good — off
        // screen, still mounted, its scrim still over the editor.
        //
        // The question the guard has to ask is about the exit happening now, not the policy the
        // open recorded.
        val exiting = presence.phase == EditorUtilityPresencePhase.EXITING
        if (shell == EditorUtilityShell.MEASURING && !exiting) return@LaunchedEffect
        val generation = presence.generation
        if (presence.motionPolicy == EditorUtilityMotionPolicy.INSTANT) {
            // No motion was promised, so there is nothing to wait for — but the exit still has to
            // be completed, or the panel never unmounts.
            if (exiting) onCompleteExit(generation)
            return@LaunchedEffect
        }
        if (reducedMotion) {
            when (presence.phase) {
                EditorUtilityPresencePhase.PENDING_ENTER,
                EditorUtilityPresencePhase.ENTERING,
                -> onAdvancePresence(generation)

                EditorUtilityPresencePhase.EXITING -> onCompleteExit(generation)
                EditorUtilityPresencePhase.CLOSED,
                EditorUtilityPresencePhase.PRESENT,
                -> Unit
            }
            return@LaunchedEffect
        }
        when (presence.phase) {
            EditorUtilityPresencePhase.PENDING_ENTER -> {
                // This effect restarts the moment `shell` stops being `MEASURING` (it is one of
                // the effect's keys), which is also the moment a remeasure settles — the same
                // frame `EditorUtilityShellFrame` first paints this element's *collapsed* width
                // rather than the measurement's fixed one, and `visibleMotion` deliberately gives
                // that collapse itself `transition: none` (see its own comment): the collapse only
                // has to be *painted*, once, before this awaits a frame and advances to `ENTERING`
                // — it must never be an animation competing with the one that follows it. Confirmed
                // on the real export with Chrome DevTools Protocol's `Animation` domain: without
                // the paired `noTransition()` on `PENDING_ENTER`, the collapse's own 180ms
                // transition and `ENTERING`'s overlapped, and the sidecar visibly narrowed past its
                // resting width before growing back out.
                //
                // A doubled `awaitEditorUtilityFrame()` made that reliable too, but at a cost this
                // effect cannot pay: it left a longer window in which a rerun of this coroutine (a
                // remeasure, a fresh open) could still be in flight against state a subsequent,
                // unrelated test had already torn down, and it was measurably responsible for
                // making other suites flaky. A synchronous layout read forces the browser to
                // commit the collapse *now*, in this microtask, rather than asking a future
                // animation frame to have done it — reading `offsetWidth` is the standard way to
                // force that flush, and it costs one style/layout pass this element was going to
                // need on the very next frame regardless.
                (document.getElementById(EDITOR_UTILITY_SURFACE_ID) as? HTMLElement)?.offsetWidth
                awaitEditorUtilityFrame()
                if (editorUtilityGenerationIsCurrent(generation)) onAdvancePresence(generation)
            }

            EditorUtilityPresencePhase.ENTERING -> {
                delay(
                    if (shell == EditorUtilityShell.SHEET) {
                        Motion.OverlayEnterMs.toLong()
                    } else {
                        Motion.StandardMs.toLong()
                    },
                )
                if (editorUtilityGenerationIsCurrent(generation)) onAdvancePresence(generation)
            }

            EditorUtilityPresencePhase.EXITING -> {
                delay(Motion.OverlayExitMs.toLong())
                if (editorUtilityGenerationIsCurrent(generation)) onCompleteExit(generation)
            }

            EditorUtilityPresencePhase.CLOSED,
            EditorUtilityPresencePhase.PRESENT,
            -> Unit
        }
    }

    val labelId = surface.labelId
    EditorUtilityShellFrame(
        shell = shell,
        reducedMotion = reducedMotion,
        onDismiss = {
            discardActivationOrigin()
            onRequestClose(EditorUtilityActionOrigin.POINTER)
        },
        labelledBy = labelId,
    ) {
        Column(EditorUtilityPaneStyle.toModifier()) {
            UtilityHeader(
                surface = surface,
                unresolvedComments = unresolvedComments,
                onSelectSurface = onSelectSurface,
                onRequestClose = onRequestClose,
                currentActivationOrigin = currentActivationOrigin,
                // The sheet is the phone shell, and the phone is where the strip runs out of room.
                compact = shell == EditorUtilityShell.SHEET,
            )
            Column(
                EditorUtilityTabPanelStyle.toModifier()
                    .attr("id", EDITOR_UTILITY_PANEL_ID)
                    // One header mode, so one panel role. Details used to be the exception —
                    // a `role="region"` under an `<h2>` while its three siblings were tabpanels
                    // under a tablist — which made "the utility panel" two different things to a
                    // screen reader depending on which of four surfaces was open.
                    .attr("role", "tabpanel")
                    .attr("aria-labelledby", labelId),
            ) {
                UtilitySurfaceContent(
                    state = state,
                    row = row,
                    surface = surface,
                    shell = shell,
                    summaryModel = summaryModel,
                    summaryExpanded = summaryExpanded,
                    onSummaryExpandedChange = { summaryExpanded = it },
                    onRequestFocus = onRequestFocus,
                    announcementsEnabled = announcementsEnabled,
                    announcementSessionKey = presence.generation,
                    onSuggestionInserted = { insertion ->
                        onSuggestionInserted(insertion, currentActivationOrigin())
                    },
                )
            }
        }
    }
}

/**
 * One header mode for four surfaces.
 *
 * Details used to be drawn as an `<h2>` title with no tablist at all, because it was reached from
 * the identity row rather than from the tabs — so the utility pane had two headers, two panel
 * roles, and one surface a reader could arrive at but never leave without closing the pane. It is
 * the fourth tab now, in the order the work goes: what the machine offers, what people asked, what
 * happened, what this string *is*.
 */
@Composable
private fun UtilityHeader(
    surface: EditorUtilitySurface,
    unresolvedComments: Int,
    onSelectSurface: (EditorUtilitySurface, EditorUtilityActionOrigin) -> Unit,
    onRequestClose: (EditorUtilityActionOrigin) -> Unit,
    currentActivationOrigin: () -> EditorUtilityActionOrigin,
    // **At 390 the four tabs plus their glyphs are wider than the strip, and the strip scrolls.**
    // Nothing said so: `Details` was cut in half at the close button's edge, and a tab you cannot
    // see is a tab you do not know is there. The glyphs are the part that can go — every one of
    // them is `aria-hidden` decoration beside a word that is the actual name, and dropping the four
    // of them returns about 110px, which is more than the overflow. The words all fit; the docked
    // pane, which has the room, keeps its glyphs.
    compact: Boolean,
) {
    val tabsLabel = t("editor.editor_utility_pane.translation_assistance", "Translation assistance")
    Row(
        EditorUtilityHeaderStyle.toModifier(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Div(
            EditorUtilityTabListStyle.toModifier().toAttrs {
                attr("role", "tablist")
                attr("aria-label", tabsLabel)
            },
        ) {
            EDITOR_UTILITY_TABS.forEach { tab ->
                UtilityTab(
                    surface = tab,
                    selected = surface == tab,
                    attention = tab == EditorUtilitySurface.DISCUSS && unresolvedComments > 0,
                    attentionCount = unresolvedComments,
                    onSelectSurface = onSelectSurface,
                    currentActivationOrigin = currentActivationOrigin,
                    compact = compact,
                )
            }
        }
        LIconButton(
            label = t("editor.editor_utility_pane.close_utility", "Close utility"),
            onClick = { onRequestClose(currentActivationOrigin()) },
            modifier = EditorCoarseTargetStyle.toModifier()
                .attr("id", EDITOR_UTILITY_CLOSE_ID)
                .attr("data-editor-coarse-target", "close"),
            size = LButtonSize.SM,
        ) {
            LCloseIcon()
        }
    }
}

@Composable
private fun UtilityTab(
    surface: EditorUtilitySurface,
    selected: Boolean,
    attention: Boolean,
    attentionCount: Int,
    onSelectSurface: (EditorUtilitySurface, EditorUtilityActionOrigin) -> Unit,
    currentActivationOrigin: () -> EditorUtilityActionOrigin,
    compact: Boolean,
) {
    val title = surface.localizedTitle()
    Button(
        EditorUtilityTabStyle.toModifier()
            .toAttrs {
                id(surface.labelId)
                attr("type", "button")
                attr("role", "tab")
                attr("aria-selected", selected.toString())
                attr("aria-controls", EDITOR_UTILITY_PANEL_ID)
                attr("tabindex", if (selected) "0" else "-1")
                onClick {
                    if (!selected) onSelectSurface(surface, currentActivationOrigin())
                }
                onKeyDown { event ->
                    moveUtilityTabFocus(event, surface, onSelectSurface)
                }
            },
    ) {
        // The glyph is `aria-hidden` and the word beside it is the name: a tab labelled by an icon
        // alone is a tab whose meaning depends on knowing this product already. Which is also why
        // the glyph, and never the word, is what the sheet drops when four tabs will not fit.
        if (!compact) {
            Span(Modifier.iconSize(IconSize.SM).toAttrs { attr("aria-hidden", "true") }) {
                surface.Glyph()
            }
        }
        Text(title)
        if (attention) {
            // **A dot, and a sentence for anyone who cannot see it.** Colour alone never carries a
            // fact here, and "3 unresolved comments" is what the dot means — said once, inside the
            // tab, so the accessible name of the control becomes "Discuss, 3 unresolved comments"
            // rather than a second control to find.
            Span(
                EditorUtilityTabDotStyle.toModifier().toAttrs {
                    attr("data-editor-utility-tab-attention", "true")
                    attr("aria-hidden", "true")
                },
            )
            Span(LVisuallyHiddenStyle.toModifier().toAttrs()) {
                Text(
                    tPlural(
                        "editor.editor_utility_pane.unresolved_comments",
                        attentionCount,
                        "{count} unresolved comment",
                        "{count} unresolved comments",
                    ),
                )
            }
        }
    }
}

/** The glyph for each utility surface, per spec §7.1. */
@Composable
private fun EditorUtilitySurface.Glyph() = when (this) {
    EditorUtilitySurface.ASSIST -> LWandSparklesIcon()
    EditorUtilitySurface.DISCUSS -> LCommentIcon()
    EditorUtilitySurface.ACTIVITY -> LHistoryIcon()
    EditorUtilitySurface.DETAILS -> LInfoIcon()
    EditorUtilitySurface.NONE -> Unit
}

private fun moveUtilityTabFocus(
    event: SyntheticKeyboardEvent,
    current: EditorUtilitySurface,
    onSelectSurface: (EditorUtilitySurface, EditorUtilityActionOrigin) -> Unit,
) {
    val currentIndex = EDITOR_UTILITY_TABS.indexOf(current)
    val targetIndex = when (event.key) {
        "ArrowRight", "ArrowDown" -> (currentIndex + 1) % EDITOR_UTILITY_TABS.size
        "ArrowLeft", "ArrowUp" -> (currentIndex - 1 + EDITOR_UTILITY_TABS.size) % EDITOR_UTILITY_TABS.size
        "Home" -> 0
        "End" -> EDITOR_UTILITY_TABS.lastIndex
        else -> return
    }
    event.preventDefault()
    val target = EDITOR_UTILITY_TABS[targetIndex]
    (document.getElementById(target.labelId) as? HTMLElement)?.focus()
    onSelectSurface(target, EditorUtilityActionOrigin.KEYBOARD)
}

@Composable
private fun UtilitySurfaceContent(
    state: TranslationEditorState,
    row: dev.lingvo.shared.KeyRow?,
    surface: EditorUtilitySurface,
    shell: EditorUtilityShell,
    summaryModel: EditorCompactSummaryModel,
    summaryExpanded: Boolean,
    onSummaryExpandedChange: (Boolean) -> Unit,
    onRequestFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
    announcementsEnabled: Boolean,
    announcementSessionKey: Long,
    onSuggestionInserted: (EditorSuggestionInsertion) -> Unit,
) {
    Column(
        EditorUtilityBodyStyle.toModifier()
            .attr("data-editor-utility-body", "true"),
    ) {
        if (shell == EditorUtilityShell.SHEET) {
            EditorCompactSummary(
                model = summaryModel,
                expanded = summaryExpanded,
                onExpandedChange = onSummaryExpandedChange,
                onRequestFocus = onRequestFocus,
                bodyOwnsScroll = true,
            )
        }
        if (row == null) {
            EditorUtilityPanelEmpty(
                name = "selection",
                title = t("editor.editor_utility_pane.no_string_selected", "No key selected"),
                description = t("editor.editor_utility_pane.choose_a_key_to_inspect_its", "Choose a key to inspect its translation utility."),
            )
        } else {
            when (surface) {
                EditorUtilitySurface.ASSIST -> EditorAssistContent(
                    state = state,
                    row = row,
                    onSuggestionInserted = onSuggestionInserted,
                    announcementsEnabled = announcementsEnabled,
                    announcementSessionKey = announcementSessionKey,
                )

                EditorUtilitySurface.DISCUSS -> EditorDiscussContent(state, row)
                EditorUtilitySurface.ACTIVITY -> EditorActivityContent(state, row)
                EditorUtilitySurface.DETAILS -> EditorDetails(state, row)
                EditorUtilitySurface.NONE -> Unit
            }
        }
    }
}

@Composable
internal fun EditorUtilityPanelEmpty(
    name: String,
    title: String,
    description: String,
) {
    Column(
        EditorUtilityEmptyStyle.toModifier()
            .attr("data-editor-panel-empty", name),
    ) {
        org.jetbrains.compose.web.dom.Span(EditorUtilityEmptyTitleStyle.toModifier().toAttrs()) {
            Text(title)
        }
        org.jetbrains.compose.web.dom.Span(Modifier.toAttrs()) { Text(description) }
    }
}

@Composable
private fun EditorUtilitySurface.localizedTitle(): String = when (this) {
    EditorUtilitySurface.ASSIST -> t("editor.editor_utility_pane.assist", "Assist")
    EditorUtilitySurface.DISCUSS -> t("editor.editor_utility_pane.discuss", "Discuss")
    EditorUtilitySurface.ACTIVITY -> t("editor.editor_utility_pane.activity", "Activity")
    EditorUtilitySurface.DETAILS -> t("editor.editor_utility_pane.details", "Details")
    EditorUtilitySurface.NONE -> t("editor.editor_utility_pane.utility", "Utility")
}

private val EditorUtilitySurface.labelId: String
    get() = when (this) {
        EditorUtilitySurface.ASSIST -> EDITOR_UTILITY_TAB_ASSIST_ID
        EditorUtilitySurface.DISCUSS -> EDITOR_UTILITY_TAB_DISCUSS_ID
        EditorUtilitySurface.ACTIVITY -> EDITOR_UTILITY_TAB_ACTIVITY_ID
        EditorUtilitySurface.DETAILS -> EDITOR_UTILITY_DETAILS_LABEL_ID
        EditorUtilitySurface.NONE -> EDITOR_UTILITY_TAB_ASSIST_ID
    }

private val EDITOR_UTILITY_TABS = listOf(
    EditorUtilitySurface.ASSIST,
    EditorUtilitySurface.DISCUSS,
    EditorUtilitySurface.ACTIVITY,
    EditorUtilitySurface.DETAILS,
)

private suspend fun awaitEditorUtilityFrame() {
    suspendCancellableCoroutine { continuation ->
        window.requestAnimationFrame {
            if (continuation.isActive) continuation.resume(Unit)
        }
    }
}

private fun editorUtilityGenerationIsCurrent(generation: Long): Boolean =
    document.getElementById(EDITOR_UTILITY_SURFACE_ID)
        ?.getAttribute("data-presence-generation") == generation.toString()
